/**
 * ResidentViolationForm — type, location, description, up to 3 photos,
 * editable observation datetime. Returns a reference number on submit.
 */
import { useState } from 'react';
import { Camera, X, CheckCircle2 } from 'lucide-react';
import { apiFetch } from '../../lib/apiFetch';
import { toast } from 'sonner';

const GOLD = '#C9A962';
const inputCls = 'w-full rounded-lg bg-zinc-900 border border-zinc-700 px-3 py-3 text-white text-sm focus:border-[#C9A962] focus:outline-none';
const labelCls = 'text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block';

function nowLocal() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export default function ResidentViolationForm({ violationTypes, onSubmitted }) {
  const [f, setF] = useState({ violation_type: '', building: '', floor: '', area: '', description: '', observed_at: nowLocal() });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const addPhotos = async (e) => {
    const files = [...(e.target.files || [])].slice(0, 3 - photos.length);
    if (files.length === 0) return;
    const fd = new FormData();
    files.forEach((file) => fd.append('files', file));
    try {
      const res = await apiFetch('/api/resident/violations/photos', { method: 'POST', body: fd });
      setPhotos((p) => [...p, ...res.photos.map((ph) => ({ ...ph, label: '' }))].slice(0, 3));
      toast.success(`${res.photos.length} photo(s) attached — add a label for each`);
    } catch (err) { toast.error(err.detail || 'Photo upload failed'); }
    e.target.value = '';
  };

  const setLabel = (path, label) =>
    setPhotos((p) => p.map((ph) => (ph.path === path ? { ...ph, label } : ph)));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.violation_type) { toast.error('Select a violation type'); return; }
    if (photos.some((p) => !(p.label || '').trim())) {
      toast.error('Please label each photo — what does it show?');
      return;
    }
    setBusy(true);
    try {
      const res = await apiFetch('/api/resident/violations', { method: 'POST', body: {
        ...f, observed_at: new Date(f.observed_at).toISOString(), photos } });
      setReceipt(res.reference);
    } catch (err) { toast.error(err.detail || 'Submission failed'); }
    finally { setBusy(false); }
  };

  if (receipt) {
    return (
      <div className="rounded-2xl border border-emerald-700 bg-emerald-950/20 p-6 text-center" data-testid="resident-violation-receipt">
        <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-500 mb-3" />
        <h2 className="text-lg font-black uppercase text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Report Received</h2>
        <p className="text-xs text-zinc-400 mt-2">Property management and NECS have been notified.</p>
        <p className="text-sm font-black mt-3 tracking-widest" style={{ color: GOLD }} data-testid="resident-violation-reference">Reference: {receipt}</p>
        <button onClick={onSubmitted} className="mt-5 px-6 py-2.5 rounded-full font-black uppercase tracking-widest text-xs" style={{ backgroundColor: GOLD, color: '#1a1a1a' }} data-testid="resident-receipt-done-btn">
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="resident-violation-form">
      <h2 className="text-sm font-black uppercase tracking-[0.25em]" style={{ color: GOLD }}>Submit a Violation</h2>
      <div><label className={labelCls}>Violation Type</label>
        <select required className={inputCls} value={f.violation_type} onChange={set('violation_type')} data-testid="rv-type">
          <option value="">Select type…</option>
          {violationTypes.map((t) => <option key={t} value={t}>{t}</option>)}
        </select></div>
      <div className="grid grid-cols-3 gap-2">
        <div><label className={labelCls}>Building</label>
          <input className={inputCls} value={f.building} onChange={set('building')} data-testid="rv-building" /></div>
        <div><label className={labelCls}>Floor</label>
          <input className={inputCls} value={f.floor} onChange={set('floor')} data-testid="rv-floor" /></div>
        <div><label className={labelCls}>Area</label>
          <input className={inputCls} placeholder="Breezeway…" value={f.area} onChange={set('area')} data-testid="rv-area" /></div>
      </div>
      <div><label className={labelCls}>Description</label>
        <textarea required minLength={5} className={`${inputCls} min-h-[100px]`} placeholder="Describe what you observed…" value={f.description} onChange={set('description')} data-testid="rv-description" /></div>
      <div><label className={labelCls}>Date &amp; Time Observed</label>
        <input type="datetime-local" className={inputCls} value={f.observed_at} onChange={set('observed_at')} data-testid="rv-observed-at" /></div>

      <div>
        <label className={labelCls}>Photos (optional, up to 3)</label>
        <div className="space-y-2">
          {photos.map((p, i) => (
            <div key={p.path} className="rounded-lg bg-zinc-900 border border-zinc-700 p-2.5" data-testid={`rv-photo-chip-${i}`}>
              <div className="flex items-center gap-2 text-[11px] text-zinc-300">
                <Camera className="h-3.5 w-3.5 shrink-0" style={{ color: GOLD }} />
                <span className="truncate flex-1">{p.filename}</span>
                <button type="button" onClick={() => setPhotos((ph) => ph.filter((x) => x.path !== p.path))} data-testid={`rv-photo-remove-${i}`}>
                  <X className="h-3.5 w-3.5 text-zinc-500" />
                </button>
              </div>
              <input required placeholder='What does this photo show? e.g. "damaged bin lid"'
                value={p.label} onChange={(e) => setLabel(p.path, e.target.value)}
                className="mt-2 w-full rounded bg-zinc-800 border border-zinc-600 px-2 py-1.5 text-white text-xs focus:border-[#C9A962] focus:outline-none"
                data-testid={`rv-photo-label-${i}`} />
            </div>
          ))}
          {photos.length < 3 && (
            <label className="rounded-lg border border-dashed border-zinc-600 px-4 py-2.5 text-[11px] text-zinc-400 cursor-pointer flex items-center gap-2 hover:border-[#C9A962] w-fit">
              <Camera className="h-4 w-4" /> Add photo
              <input type="file" accept="image/*" multiple className="hidden" onChange={addPhotos} data-testid="rv-photo-input" />
            </label>
          )}
        </div>
      </div>

      <button type="submit" disabled={busy}
        className="w-full py-3.5 rounded-full font-black uppercase tracking-widest text-sm disabled:opacity-50"
        style={{ backgroundColor: GOLD, color: '#1a1a1a' }} data-testid="rv-submit-btn">
        {busy ? 'Submitting…' : 'Submit Report'}
      </button>
    </form>
  );
}
